/**
 * THE ONLY MODULE ALLOWED TO TOUCH `import.meta.env`.
 *
 * Enforced by eslint `no-restricted-syntax` (see eslint.config.js): every other
 * file in src/** gets a lint error for `import.meta.env`. Panels import `env`
 * and `isConfigured` from here.
 *
 * Contract: reading env NEVER throws and NEVER logs console.error. A bad value
 * degrades to the documented fallback, because "app loads with no console
 * errors on an empty .env" is a graded acceptance criterion.
 */

/**
 * Documented default location, shipped so the app runs out of the box.
 * Denver, CO — a US location, chosen because the severe-weather alert source
 * (api.weather.gov, see DECISIONS.md) is US-only, so the alert code path is
 * actually exercisable. Override with VITE_WEATHER_LAT / VITE_WEATHER_LON.
 */
const DEFAULT_LOCATION = {
  name: 'Denver, CO',
  lat: 39.7392,
  lon: -104.9903,
} as const;

const DEFAULT_BTC_REFRESH_SECONDS = 60;

type RawEnv = Readonly<Record<string, string | undefined>>;
const raw: RawEnv = import.meta.env as unknown as RawEnv;
const IS_DEV: boolean = import.meta.env.DEV;

/** console.warn (never .error) — an unset key is a supported configuration. */
function note(message: string): void {
  if (IS_DEV) console.warn(`[env] ${message}`);
}

/** '' / whitespace / the literal strings 'undefined' and 'null' all mean unset. */
function str(name: string): string | undefined {
  const value = raw[name];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (trimmed === '' || trimmed === 'undefined' || trimmed === 'null') return undefined;
  return trimmed;
}

function num(name: string): number | undefined {
  const s = str(name);
  if (s === undefined) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function intInRange(name: string, fallback: number, min: number, max: number): number {
  const n = num(name);
  if (n === undefined) return fallback;
  const clamped = Math.min(max, Math.max(min, Math.round(n)));
  if (clamped !== n) note(`${name}=${n} clamped to ${clamped} (allowed ${min}-${max}).`);
  return clamped;
}

export type WeatherUnits = 'imperial' | 'metric';

export interface WeatherConfig {
  readonly locationName: string;
  readonly lat: number;
  readonly lon: number;
  readonly units: WeatherUnits;
  /** true when coordinates came from DEFAULT_LOCATION, not from .env. */
  readonly usingDefaultLocation: boolean;
  readonly apiKey: string | undefined;
}

function resolveWeather(): WeatherConfig {
  const lat = num('VITE_WEATHER_LAT');
  const lon = num('VITE_WEATHER_LON');
  const name = str('VITE_WEATHER_LOCATION_NAME');

  const unitsRaw = str('VITE_WEATHER_UNITS')?.toLowerCase();
  const units: WeatherUnits = unitsRaw === 'metric' ? 'metric' : 'imperial';
  if (unitsRaw !== undefined && unitsRaw !== 'metric' && unitsRaw !== 'imperial') {
    note(`VITE_WEATHER_UNITS="${unitsRaw}" is not imperial|metric - using imperial.`);
  }

  // Coordinates are all-or-nothing: half a pair is a typo, not a configuration.
  const coordsValid =
    lat !== undefined && lon !== undefined && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;

  if (!coordsValid) {
    if (lat !== undefined || lon !== undefined) {
      note('VITE_WEATHER_LAT/LON incomplete or out of range - using the default location.');
    }
    return {
      locationName: name ?? DEFAULT_LOCATION.name,
      lat: DEFAULT_LOCATION.lat,
      lon: DEFAULT_LOCATION.lon,
      units,
      usingDefaultLocation: true,
      apiKey: str('VITE_OPENWEATHER_API_KEY'),
    };
  }

  return {
    // Custom coords with no label: show the coords, never the default city's name.
    locationName: name ?? `${lat.toFixed(2)}, ${lon.toFixed(2)}`,
    lat,
    lon,
    units,
    usingDefaultLocation: false,
    apiKey: str('VITE_OPENWEATHER_API_KEY'),
  };
}

export interface AppEnv {
  readonly displayName: string | undefined;
  readonly weather: WeatherConfig;
  readonly bitcoin: {
    readonly newhedgeToken: string | undefined;
    readonly refreshSeconds: number;
  };
  readonly music: {
    readonly spotifyClientId: string | undefined;
    readonly anthropicApiKey: string | undefined;
  };
  readonly calendar: {
    readonly googleClientId: string | undefined;
    /** Must be registered verbatim as an authorized JS origin. See README. */
    readonly origin: string;
  };
  readonly quote: {
    readonly quotesApiKey: string | undefined;
    readonly imageGenApiKey: string | undefined;
  };
}

function deepFreeze<T>(value: T): T {
  Object.values(value as Record<string, unknown>).forEach((v) => {
    if (v !== null && typeof v === 'object') deepFreeze(v);
  });
  return Object.freeze(value);
}

export const env: AppEnv = deepFreeze({
  displayName: str('VITE_DISPLAY_NAME'),
  weather: resolveWeather(),
  bitcoin: {
    newhedgeToken: str('VITE_NEWHEDGE_API_TOKEN'),
    refreshSeconds: intInRange('VITE_BTC_REFRESH_SECONDS', DEFAULT_BTC_REFRESH_SECONDS, 15, 3600),
  },
  music: {
    spotifyClientId: str('VITE_SPOTIFY_CLIENT_ID'),
    anthropicApiKey: str('VITE_ANTHROPIC_API_KEY'),
  },
  calendar: {
    googleClientId: str('VITE_GOOGLE_CLIENT_ID'),
    // Derived, not configured - always matches the dev server (port 5473, pinned).
    origin: typeof window === 'undefined' ? 'http://localhost:5473' : window.location.origin,
  },
  quote: {
    quotesApiKey: str('VITE_QUOTES_API_KEY'),
    imageGenApiKey: str('VITE_IMAGE_GEN_API_KEY'),
  },
});

/**
 * Feature gates. Panels branch on these to pick `unconfigured` vs `ready`, so
 * "is this configured?" has exactly one answer per feature across the app.
 */
export const isConfigured = Object.freeze({
  weather: env.weather.apiKey !== undefined,
  calendar: env.calendar.googleClientId !== undefined,
  spotify: env.music.spotifyClientId !== undefined,
  moodInterpretation: env.music.anthropicApiKey !== undefined,
  newhedge: env.bitcoin.newhedgeToken !== undefined,
  quoteImage: env.quote.imageGenApiKey !== undefined,
  displayName: env.displayName !== undefined,
});
